/**
 * Themes are Zed theme families — the JSON format Zed's extension registry uses
 * (https://zed.dev/schema/themes/v0.2.0.json) — so a popular theme's published
 * Zed file drops into `packages/core/themes/` unchanged. This module turns one
 * of those themes into the fixed set of Rivets tokens every client paints with:
 * the web's `--surface-0` custom properties, `RivetsTheme.surface0` on native.
 *
 * Every token reads a Zed style key first and falls back to a derivation from
 * tokens already resolved, so a sparse theme still produces a complete palette.
 * A theme can pin any Rivets token exactly with a `rivets.<token>` style key —
 * Zed ignores keys it does not know, so a pinned theme stays a valid Zed theme.
 * The built-in Rivets theme uses pins to reproduce its hand-tuned values.
 *
 * `bun run themes:build` resolves every family into `themes-builtin.ts` and the
 * native `RivetsThemes.generated.swift`; see `packages/core/themes/README.md`.
 */

export type ThemeAppearance = "dark" | "light";

export interface ZedSyntaxStyle {
  color?: string | null;
  font_style?: string | null;
  font_weight?: number | null;
}

export interface ZedTheme {
  name: string;
  appearance: ThemeAppearance;
  style: Record<string, unknown> & {
    syntax?: Record<string, ZedSyntaxStyle | undefined>;
    players?: Array<{ cursor?: string | null } | undefined>;
  };
}

export interface ZedThemeFamily {
  $schema?: string;
  name: string;
  author?: string;
  themes: ZedTheme[];
}

/** App tokens, in resolution order: a derivation may only read tokens above it.
 * Names are the web's custom properties without the leading `--`. The last
 * group exists only on native, which fills solid where the web layers alpha. */
export const THEME_COLOR_TOKENS = [
  "surface-1",
  "surface-0",
  "sidebar-surface",
  "text-primary",
  "text-secondary",
  "text-tertiary",
  "surface-2",
  "surface-3",
  "border-subtle",
  "border-strong",
  "accent",
  "accent-hover",
  "on-accent",
  "accent-soft",
  "focus-ring",
  "success",
  "success-soft",
  "warning",
  "warning-soft",
  "danger",
  "danger-soft",
  "primary-fill",
  "primary-label",
  "glass-fill",
  "glass-border",
  "find-match",
  "find-match-current",
  "accent-soft-solid",
  "success-soft-solid",
  "warning-soft-solid",
  "danger-soft-solid",
  "on-cta",
  "glass-tint",
] as const;
export type ThemeColorToken = (typeof THEME_COLOR_TOKENS)[number];

/** Syntax roles. Each maps onto a group of lezer `tok-*` classes on the web and
 * highlight.js scopes on native. */
export const THEME_SYNTAX_TOKENS = [
  "keyword",
  "string",
  "number",
  "comment",
  "variable",
  "property",
  "function",
  "type",
  "tag",
  "heading",
  "punctuation",
  "operator",
  "link",
] as const;
export type ThemeSyntaxToken = (typeof THEME_SYNTAX_TOKENS)[number];

/** The sixteen ANSI colours, in terminal index order. */
export const THEME_ANSI_NAMES = [
  "black", "red", "green", "yellow", "blue", "magenta", "cyan", "white",
  "bright_black", "bright_red", "bright_green", "bright_yellow", "bright_blue", "bright_magenta", "bright_cyan", "bright_white",
] as const;

export interface ResolvedTheme {
  /** Stable slug of `name`; what a preference stores. */
  id: string;
  name: string;
  family: string;
  author?: string;
  appearance: ThemeAppearance;
  /** Lowercase `#rrggbb`, or `#rrggbbaa` when translucent. */
  colors: Record<ThemeColorToken, string>;
  syntax: Record<ThemeSyntaxToken, string>;
  /** Sixteen colours, indexed like `THEME_ANSI_NAMES`. */
  ansi: string[];
}

export const DEFAULT_DARK_THEME_ID = "rivets-dark";
export const DEFAULT_LIGHT_THEME_ID = "rivets-light";

// ---------------------------------------------------------------------------
// Colour arithmetic. Channels are 0–255, alpha 0–1.

export interface Rgba { r: number; g: number; b: number; a: number }

const BLACK: Rgba = { r: 0, g: 0, b: 0, a: 1 };
const WHITE: Rgba = { r: 255, g: 255, b: 255, a: 1 };

/** Accepts `#rgb`, `#rgba`, `#rrggbb` and `#rrggbbaa`, with or without the
 * hash — published Zed themes contain all of them. */
export function parseColor(value: unknown): Rgba | null {
  if (typeof value !== "string") return null;
  let hex = value.trim().replace(/^#/, "");
  if (!/^[0-9a-f]+$/i.test(hex)) return null;
  if (hex.length === 3 || hex.length === 4) hex = [...hex].map((c) => c + c).join("");
  if (hex.length !== 6 && hex.length !== 8) return null;
  const n = (i: number) => parseInt(hex.slice(i, i + 2), 16);
  return { r: n(0), g: n(2), b: n(4), a: hex.length === 8 ? n(6) / 255 : 1 };
}

export function formatColor(color: Rgba): string {
  const h = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0");
  const alpha = Math.round(Math.min(1, Math.max(0, color.a)) * 255);
  return `#${h(color.r)}${h(color.g)}${h(color.b)}${alpha === 255 ? "" : h(alpha)}`;
}

/** `amount` of the way from `from` to `to`, alpha included. */
export function mix(from: Rgba, to: Rgba, amount: number): Rgba {
  const t = (a: number, b: number) => a + (b - a) * amount;
  return { r: t(from.r, to.r), g: t(from.g, to.g), b: t(from.b, to.b), a: t(from.a, to.a) };
}

export function withAlpha(color: Rgba, alpha: number): Rgba {
  return { ...color, a: alpha };
}

/** `top` painted over `bottom`; opaque whenever `bottom` is. */
export function composite(top: Rgba, bottom: Rgba): Rgba {
  const a = top.a + bottom.a * (1 - top.a);
  if (a === 0) return { r: 0, g: 0, b: 0, a: 0 };
  const c = (t: number, b: number) => (t * top.a + b * bottom.a * (1 - top.a)) / a;
  return { r: c(top.r, bottom.r), g: c(top.g, bottom.g), b: c(top.b, bottom.b), a };
}

/** WCAG relative luminance, ignoring alpha. */
export function luminance(color: Rgba): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(color.r) + 0.7152 * lin(color.g) + 0.0722 * lin(color.b);
}

export function contrast(a: Rgba, b: Rgba): number {
  const [la, lb] = [luminance(a), luminance(b)];
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Moves `color` toward `toward` until it reaches `minimum` contrast against
 * `background`, or arrives. */
function ensureContrast(color: Rgba, background: Rgba, toward: Rgba, minimum: number): Rgba {
  let result = color;
  for (let step = 1; step <= 20 && contrast(result, background) < minimum; step++) result = mix(color, toward, step / 20);
  return result;
}

// ---------------------------------------------------------------------------
// Resolution

/** `Catppuccin Frappé` → `catppuccin-frappe`. */
export function themeId(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type Colors = Partial<Record<ThemeColorToken, Rgba>>;

interface Context {
  theme: ZedTheme;
  dark: boolean;
  colors: Colors;
  /** Opaque ground everything else is flattened onto. */
  ground: Rgba;
}

function styleColor(theme: ZedTheme, key: string): Rgba | null {
  return parseColor(theme.style[key]);
}

function firstStyleColor(theme: ZedTheme, keys: readonly string[]): Rgba | null {
  for (const key of keys) {
    const color = styleColor(theme, key);
    if (color) return color;
  }
  return null;
}

function syntaxColor(theme: ZedTheme, keys: readonly string[]): Rgba | null {
  for (const key of keys) {
    const color = parseColor(theme.style.syntax?.[key]?.color);
    if (color) return color;
  }
  return null;
}

/** Tokens that are painted as solid fills: flattened onto the ground so a
 * translucent Zed value (Dracula's window is 20% opaque) still reads as the
 * colour it was designed to look like. */
const OPAQUE = new Set<ThemeColorToken>([
  "surface-0", "surface-1", "surface-2", "surface-3", "sidebar-surface",
  "text-primary", "text-secondary", "text-tertiary",
  "accent", "accent-hover", "on-accent", "success", "warning", "danger",
  "primary-fill", "primary-label",
  "accent-soft-solid", "success-soft-solid", "warning-soft-solid", "danger-soft-solid", "on-cta",
]);

type Rule = { keys?: readonly string[]; derive: (ctx: Context, get: (token: ThemeColorToken) => Rgba) => Rgba };

/** Fixed alphas the Rivets design uses for its layered fills. */
const SOFT_ALPHA = { dark: 0.12, light: 0.10 };
const ACCENT_SOFT_ALPHA = { dark: 0.10, light: 0.08 };

/** Controls sit a step toward the text from whichever pane surface is nearer
 * to it, so a button reads as raised on both the canvas and the content. */
function controlBase(ctx: Context, get: (token: ThemeColorToken) => Rgba): Rgba {
  const text = get("text-primary");
  const [s0, s1] = [get("surface-0"), get("surface-1")];
  return contrast(s0, text) < contrast(s1, text) ? s0 : s1;
}

const RULES: Record<ThemeColorToken, Rule> = {
  "surface-1": { keys: ["editor.background", "background"], derive: (ctx) => ctx.ground },
  "surface-0": { keys: ["panel.background", "background"], derive: (_, get) => get("surface-1") },
  "sidebar-surface": { derive: (_, get) => get("surface-0") },
  "text-primary": { keys: ["text", "editor.foreground"], derive: (ctx) => (ctx.dark ? WHITE : BLACK) },
  "text-secondary": { keys: ["text.muted"], derive: (_, get) => mix(get("text-primary"), get("surface-1"), 0.3) },
  "text-tertiary": { keys: ["text.placeholder", "text.disabled"], derive: (_, get) => mix(get("text-secondary"), get("surface-1"), 0.25) },
  "surface-2": { derive: (ctx, get) => mix(controlBase(ctx, get), get("text-primary"), ctx.dark ? 0.035 : 0.08) },
  "surface-3": { derive: (ctx, get) => mix(controlBase(ctx, get), get("text-primary"), ctx.dark ? 0.075 : 0.13) },
  "border-subtle": { keys: ["border.variant"], derive: (_, get) => withAlpha(get("text-primary"), 0.06) },
  "border-strong": { keys: ["border"], derive: (_, get) => withAlpha(get("text-primary"), 0.12) },
  accent: {
    keys: ["text.accent", "icon.accent", "border.focused"],
    derive: (ctx) => parseColor(ctx.theme.style.players?.[0]?.cursor) ?? (ctx.dark ? parseColor("#8ab4f8")! : parseColor("#2f66c5")!),
  },
  "accent-hover": { derive: (ctx, get) => (ctx.dark ? mix(get("accent"), WHITE, 0.2) : mix(get("accent"), BLACK, 0.15)) },
  "on-accent": {
    derive: (_, get) => {
      const [accent, surface, text] = [get("accent"), get("surface-1"), get("text-primary")];
      return contrast(surface, accent) >= contrast(text, accent) ? surface : text;
    },
  },
  "accent-soft": { derive: (ctx, get) => withAlpha(get("accent"), ACCENT_SOFT_ALPHA[ctx.theme.appearance]) },
  "focus-ring": { derive: (_, get) => withAlpha(get("accent"), 0.3) },
  success: { keys: ["success", "created", "version_control.added", "terminal.ansi.green"], derive: (ctx) => parseColor(ctx.dark ? "#3ccb8e" : "#137a55")! },
  "success-soft": { derive: (ctx, get) => withAlpha(get("success"), SOFT_ALPHA[ctx.theme.appearance]) },
  warning: { keys: ["warning", "modified", "version_control.modified", "terminal.ansi.yellow"], derive: (ctx) => parseColor(ctx.dark ? "#f2b84b" : "#9a650d")! },
  "warning-soft": { derive: (ctx, get) => withAlpha(get("warning"), SOFT_ALPHA[ctx.theme.appearance]) },
  danger: { keys: ["error", "deleted", "version_control.deleted", "terminal.ansi.red"], derive: (ctx) => parseColor(ctx.dark ? "#f06a72" : "#b93845")! },
  "danger-soft": { derive: (ctx, get) => withAlpha(get("danger"), SOFT_ALPHA[ctx.theme.appearance]) },
  "primary-fill": { derive: (_, get) => get("text-primary") },
  "primary-label": { derive: (_, get) => get("surface-1") },
  "glass-fill": { derive: (ctx, get) => withAlpha(get("sidebar-surface"), ctx.dark ? 0.78 : 0.84) },
  "glass-border": { derive: (_, get) => withAlpha(get("text-primary"), 0.1) },
  "find-match": { keys: ["search.match_background"], derive: (ctx, get) => withAlpha(get("warning"), ctx.dark ? 0.28 : 0.35) },
  "find-match-current": { keys: ["search.active_match_background"], derive: (_, get) => withAlpha(get("warning"), 0.6) },
  "accent-soft-solid": { derive: (_, get) => composite(get("accent-soft"), get("surface-1")) },
  "success-soft-solid": { derive: (_, get) => composite(get("success-soft"), get("surface-1")) },
  "warning-soft-solid": { derive: (_, get) => composite(get("warning-soft"), get("surface-1")) },
  "danger-soft-solid": { derive: (_, get) => composite(get("danger-soft"), get("surface-1")) },
  "on-cta": { derive: (_, get) => get("primary-label") },
  "glass-tint": { derive: (_, get) => withAlpha(get("surface-1"), 0.78) },
};

const SYNTAX_RULES: Record<ThemeSyntaxToken, { keys: readonly string[]; fallback: (colors: Record<ThemeColorToken, Rgba>, syntax: Partial<Record<ThemeSyntaxToken, Rgba>>) => Rgba }> = {
  keyword: { keys: ["keyword"], fallback: (c) => c.accent },
  string: { keys: ["string"], fallback: (c) => c.success },
  number: { keys: ["number", "constant", "boolean"], fallback: (c) => c.warning },
  comment: { keys: ["comment", "comment.doc"], fallback: (c) => c["text-tertiary"] },
  variable: { keys: ["variable"], fallback: (c) => c["text-primary"] },
  property: { keys: ["property", "variable"], fallback: (c) => c["text-primary"] },
  function: { keys: ["function", "function.method"], fallback: (c) => c.accent },
  type: { keys: ["type", "constructor"], fallback: (_, s) => s.function! },
  tag: { keys: ["tag"], fallback: (c) => c.danger },
  heading: { keys: ["title", "tag"], fallback: (_, s) => s.tag! },
  punctuation: { keys: ["punctuation", "punctuation.delimiter"], fallback: (c) => c["text-secondary"] },
  operator: { keys: ["operator"], fallback: (_, s) => s.punctuation! },
  link: { keys: ["link_uri", "link_text"], fallback: (c) => c.accent },
};

export class ThemeResolutionError extends Error {}

export function resolveTheme(family: ZedThemeFamily, theme: ZedTheme): ResolvedTheme {
  if (theme.appearance !== "dark" && theme.appearance !== "light") {
    throw new ThemeResolutionError(`${theme.name}: appearance must be "dark" or "light"`);
  }
  const dark = theme.appearance === "dark";
  const base = dark ? BLACK : WHITE;
  const editor = firstStyleColor(theme, ["editor.background", "background"]);
  if (!editor) throw new ThemeResolutionError(`${theme.name}: needs "editor.background" or "background"`);
  const ctx: Context = { theme, dark, colors: {}, ground: composite(editor, base) };

  const get = (token: ThemeColorToken): Rgba => {
    const color = ctx.colors[token];
    if (!color) throw new ThemeResolutionError(`${theme.name}: ${token} read before it was resolved`);
    return color;
  };

  for (const token of THEME_COLOR_TOKENS) {
    const rule = RULES[token];
    let color = styleColor(theme, `rivets.${token}`) ?? (rule.keys ? firstStyleColor(theme, rule.keys) : null) ?? rule.derive(ctx, get);
    if (OPAQUE.has(token)) {
      // Surfaces flatten onto the ground; everything painted on them flattens
      // onto the content surface they are read against.
      // The content surface is the ground itself, so it flattens onto the
      // bare appearance base; flattening it onto the ground would apply its
      // alpha twice.
      const under = token === "surface-1" ? base
        : token.startsWith("surface") || token === "sidebar-surface" ? ctx.ground
        : ctx.colors["surface-1"] ?? ctx.ground;
      color = composite(color, under);
    }
    // Secondary and tertiary text carry real information (timestamps, counts,
    // metadata), so they keep a legibility floor that editor placeholder
    // colours — which is what Zed's keys are — do not always meet.
    if (token === "text-secondary") color = ensureContrast(color, get("surface-1"), get("text-primary"), 4.5);
    if (token === "text-tertiary") color = ensureContrast(color, get("surface-1"), get("text-secondary"), 3);
    ctx.colors[token] = color;
  }
  const colors = ctx.colors as Record<ThemeColorToken, Rgba>;

  const syntax: Partial<Record<ThemeSyntaxToken, Rgba>> = {};
  for (const token of THEME_SYNTAX_TOKENS) {
    const rule = SYNTAX_RULES[token];
    syntax[token] = composite(syntaxColor(theme, rule.keys) ?? rule.fallback(colors, syntax), colors["surface-1"]);
  }

  const ansiFallback = (index: number): Rgba => {
    const bright = index >= 8;
    const baseIndex = index % 8;
    const normal: Rgba[] = [
      colors["surface-2"], colors.danger, colors.success, colors.warning, colors.accent,
      syntax.keyword!, mix(colors.accent, colors.success, 0.5), colors["text-secondary"],
    ];
    if (index === 8) return colors["text-tertiary"];
    if (index === 15) return colors["text-primary"];
    const color = normal[baseIndex]!;
    return bright ? mix(color, dark ? WHITE : BLACK, 0.2) : color;
  };
  const ansi = THEME_ANSI_NAMES.map((name, index) =>
    formatColor(composite(styleColor(theme, `terminal.ansi.${name}`) ?? ansiFallback(index), colors["surface-1"])),
  );

  return {
    id: themeId(theme.name),
    name: theme.name,
    family: family.name,
    ...(family.author ? { author: family.author } : {}),
    appearance: theme.appearance,
    colors: Object.fromEntries(THEME_COLOR_TOKENS.map((token) => [token, formatColor(colors[token])])) as Record<ThemeColorToken, string>,
    syntax: Object.fromEntries(THEME_SYNTAX_TOKENS.map((token) => [token, formatColor(syntax[token]!)])) as Record<ThemeSyntaxToken, string>,
    ansi,
  };
}

export function resolveThemeFamily(family: ZedThemeFamily): ResolvedTheme[] {
  if (!family || typeof family.name !== "string" || !Array.isArray(family.themes)) {
    throw new ThemeResolutionError("A theme family needs a name and a themes array");
  }
  return family.themes.map((theme) => resolveTheme(family, theme));
}

// ---------------------------------------------------------------------------
// Selection

export type AppearancePreference = "dark" | "light" | "auto";

export interface ThemePreference {
  appearance: AppearancePreference;
  darkThemeId: string;
  lightThemeId: string;
}

/** The theme to paint with. An unknown id — a theme since removed — falls back
 * to the Rivets theme of the same appearance, never to one of the other. */
export function selectTheme(
  themes: readonly ResolvedTheme[],
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  const appearance: ThemeAppearance = preference.appearance === "auto" ? (systemPrefersDark ? "dark" : "light") : preference.appearance;
  const wanted = appearance === "dark" ? preference.darkThemeId : preference.lightThemeId;
  const fallback = appearance === "dark" ? DEFAULT_DARK_THEME_ID : DEFAULT_LIGHT_THEME_ID;
  return (
    themes.find((t) => t.id === wanted && t.appearance === appearance) ??
    themes.find((t) => t.id === fallback) ??
    themes.find((t) => t.appearance === appearance) ??
    themes[0]!
  );
}

/** Custom properties a resolved theme sets on the web root. The native-only
 * tokens are left out; syntax colours become `--syntax-*`. */
export const NATIVE_ONLY_TOKENS: ReadonlySet<ThemeColorToken> = new Set([
  "accent-soft-solid", "success-soft-solid", "warning-soft-solid", "danger-soft-solid", "on-cta", "glass-tint",
]);

export function themeCssVariables(theme: ResolvedTheme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const token of THEME_COLOR_TOKENS) {
    if (!NATIVE_ONLY_TOKENS.has(token)) vars[`--${token}`] = theme.colors[token];
  }
  for (const token of THEME_SYNTAX_TOKENS) vars[`--syntax-${token}`] = theme.syntax[token];
  return vars;
}
